import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
   Box,
   Input,
   InputGroup,
   InputLeftElement,
   List,
   ListItem,
   useColorModeValue,
   HStack,
   Kbd,
   Modal,
   ModalOverlay,
   ModalContent,
   useDisclosure,
   Text,
   Image,
   Flex,
   Td,
   Th,
   Tr,
   Table,
   Tbody,
   Thead,
   Badge,
   IconButton,
   VStack,
   ModalCloseButton,
   useColorMode,
   Skeleton,
   SkeletonText,
   SkeletonCircle,
   Tooltip,
   Button,
   Spinner,
} from '@chakra-ui/react';
import { CloseIcon, QuestionOutlineIcon, SearchIcon, ViewIcon, WarningIcon } from '@chakra-ui/icons';
import { AlertTriangle, Ban, CheckIcon, EditIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ViewSingleData from '../../../views/admin/masters/components/ViewSingleData';
import { getGlobalSearch } from '../../../redux/actions/globalSearch';
import { useGlobal, useUser } from '../../../contexts';
import { getReportOutstandingDetail } from '../../../redux/actions/reports';
import { getReportLedger } from '../../../redux/actions/reports';
import {
   masterRoutes,
   purchaseRoutes,
   saleRoutes,
   otherRoutes,
   itemRoutes,
   transportRoutes,
   reportRoutes,
} from 'routes.js';

import nofound from 'assets/img/nfts/not found.svg';
import { getSingleCustomer } from '../../../redux/actions/customer';
import {
   deletePurchaseReturnInvoice,
   getSinglePurchaseReturn,
   getSinglePurchaseReturnWithPrevAndNext,
} from '../../../redux/actions/purchaseReturnInvoice';
import SalesDetails from 'components/salesDetails/SalesDetails';
import { deleteRecord, deleteRecordForSales } from '../../../utils/util';
import {
   deleteSaleReturnInvoice,
   getSingleSalesReturnWithPrevAndNext,
} from '../../../redux/actions/salesReturnInvoice';
import { deleteSaleInvoice, getSaleInvoice, partyServiceBlock } from '../../../redux/actions/salesInvoice';
import { deletePurchaseInvoice, getPurchaseInvoice } from '../../../redux/actions/purchaseInvoice';
import { deleteIssueInventory, getIssueInventory } from '../../../redux/actions/issueInventory';
import SalesSingleDataView from '../../../views/admin/sales/Pages/SalesInvoiceMaster/SaleSingleDataView';
import { getAccountById } from '../../../redux/actions/account';
import { deleteReceiveInventory, getReceiveInventory } from '../../../redux/actions/receiveInventory';
import { getSingleItem } from '../../../redux/actions/item';
import { RiGhost2Fill } from 'react-icons/ri';
import { LuRefreshCcw } from 'react-icons/lu';

window.globalSearchModalOpen = window.globalSearchModalOpen || false;
window.globalSearchHotkeyRegistered = window.globalSearchHotkeyRegistered || false;
window.openGlobalSearchModal = null;

if (!window.globalSearchHotkeyRegistered) {
   window.globalSearchHotkeyRegistered = true;
   document.addEventListener('keydown', (event) => {
      if (event.ctrlKey && event.key === ' ') {
         event.preventDefault();
         if (!window.globalSearchModalOpen && window.openGlobalSearchModal) {
            window.openGlobalSearchModal();
         }
      }
   });
}

// Skeleton Loader Components
const SearchResultSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <Box width="100%" mb={6}>
         <Skeleton
            height="24px"
            width="120px"
            mb={3}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
         <List spacing={2}>
            {Array.from({ length: 3 }).map((_, index) => (
               <ListItem key={index}>
                  <Box
                     p={4}
                     borderRadius="xl"
                     border="2px"
                     borderColor={isDark ? 'gray.700' : 'gray.200'}
                     bg={isDark ? 'gray.800' : 'white'}>
                     <HStack spacing={3} align="start">
                        <SkeletonCircle
                           size="32px"
                           startColor={isDark ? 'gray.600' : 'gray.200'}
                           endColor={isDark ? 'gray.700' : 'gray.300'}
                        />
                        <Box flex={1}>
                           <Skeleton
                              height="16px"
                              width="70%"
                              mb={2}
                              startColor={isDark ? 'gray.600' : 'gray.200'}
                              endColor={isDark ? 'gray.700' : 'gray.300'}
                           />
                           <Skeleton
                              height="14px"
                              width="90%"
                              startColor={isDark ? 'gray.600' : 'gray.200'}
                              endColor={isDark ? 'gray.700' : 'gray.300'}
                           />
                        </Box>
                     </HStack>
                  </Box>
               </ListItem>
            ))}
         </List>
      </Box>
   );
};

const TableSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <Box
         className={`overflow-hidden rounded-xl border-2 ${isDark ? 'border-gray-700' : 'border-gray-200'} shadow-lg`}>
         <Table variant="simple" size="sm">
            <Thead
               className={
                  isDark ? 'bg-gradient-to-r from-gray-800 to-gray-750' : 'bg-gradient-to-r from-gray-50 to-gray-100'
               }>
               <Tr>
                  {Array.from({ length: 9 }).map((_, index) => (
                     <Th key={index} className={`py-4 px-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <Skeleton
                           height="16px"
                           width="80%"
                           startColor={isDark ? 'gray.600' : 'gray.200'}
                           endColor={isDark ? 'gray.700' : 'gray.300'}
                        />
                     </Th>
                  ))}
               </Tr>
            </Thead>
            <Tbody>
               {Array.from({ length: 5 }).map((_, rowIndex) => (
                  <Tr key={rowIndex}>
                     {Array.from({ length: 9 }).map((_, cellIndex) => (
                        <Td key={cellIndex} className={`py-3 px-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                           <Skeleton
                              height="14px"
                              width={cellIndex === 2 ? '90%' : '70%'}
                              startColor={isDark ? 'gray.600' : 'gray.200'}
                              endColor={isDark ? 'gray.700' : 'gray.300'}
                           />
                        </Td>
                     ))}
                  </Tr>
               ))}
            </Tbody>
         </Table>
      </Box>
   );
};

const NoResultsSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <Box textAlign="center" mt={8}>
         <Skeleton
            width="300px"
            height="300px"
            borderRadius="md"
            mx="auto"
            mb={5}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
         <Skeleton
            height="24px"
            width="200px"
            mx="auto"
            mb={2}
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
         <Skeleton
            height="16px"
            width="250px"
            mx="auto"
            startColor={isDark ? 'gray.600' : 'gray.200'}
            endColor={isDark ? 'gray.700' : 'gray.300'}
         />
      </Box>
   );
};

export function SearchBarGlobal(props) {
   const { background, borderRadius, ...rest } = props;
   const searchIconColor = useColorModeValue('gray.700', 'white');
   const inputBg = useColorModeValue('secondaryGray.300', 'gray.900');
   const inputText = useColorModeValue('gray.700', 'gray.100');
   const dispatch = useDispatch();
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [searchQuery, setSearchQuery] = useState('');
   const searchresults = useSelector((state) => state?.globalSearch?.Search || []);
   const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
   const [filteredResults, setFilteredResults] = useState([]);
   const [isSearchLoading, setIsSearchLoading] = useState(false);

   const [singleEntityWithLedger, setsingleEntityWithLedger] = useState([]);
   const [entityType, setEntityType] = useState([]);
   const [entityTypesv, setEntityTypesv] = useState('');
   const [dynamicUserDetails, setDynamicUserDetails] = useState([]);
   const [dynamicArray, setdynamicArray] = useState([]);
   const [tempSearch, setTempSearch] = useState('');
   const [isOpenSingleView, setIsOpenSingleView] = React.useState(null);
   const [isOpenPurchaseReturnSingleView, setIsOpenPurchaseReturnSingleView] = React.useState(false);
   const [menuResults, setMenuResults] = useState([]);
   const [isEditFormOpen, setIsEditFormOpen] = React.useState(false);
   const [currentViewDetail, setCurrentViewDetail] = useState(null);
   const [singleItemData, setSingleItemData] = useState(null);
   const [isReturnDetailsOpen, setIsReturnDetailsOpen] = useState(false);
   const [selectedReturnDetails, setSelectedReturnDetails] = useState(null);
   const [isBlockDetailsOpen, setIsBlockDetailsOpen] = useState(false);
   const [selectedBlockDetails, setSelectedBlockDetails] = useState(null);
   const [editingItemId, setEditingItemId] = useState(null);
   const [tempRates, setTempRates] = useState({});
   const [isRefreshing, setIsRefreshing] = useState(false);
   const inputRef = useRef(null);
   const navigate = useNavigate();
   const { user } = useUser();
   const textcolor = useColorModeValue('#000000', '#ffffff');
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   // Get all the different single entity data from store
   const receiveInventoryData = useSelector((state) => state?.receiveInventory?.single || {});
   const issueInventoryData = useSelector((state) => state?.issueInventory?.single || {});
   const purchaseReturnData = useSelector((state) => state.purchaseReturnInvoice?.purchaseReturnData);
   const salesReturnData = useSelector((state) => state.salesReturnInvoice?.saleReturnData);
   const salesInvoiceData = useSelector((state) => state.sales?.single);
   const purchaseInvoiceData = useSelector((state) => state?.purchaseInvoice?.single);
   const account = useSelector((state) => state.account.single);
   const companyId = Number(localStorage.getItem('companyId'));
   const userRights = user?.user?.userRights || user?.userRights;
   const saleInvoiceRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.saleinvoice
      : null;
   const saleReturnRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.salereturn
      : null;
   const purchaseInvoiceRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.purchaseinvoice
      : null;
   const purchaseReturnRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.purchasereturn
      : null;
   const inventoryissueRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === user?.loginhistory?.companyId)?.inventoryissue
      : null;
   const inventoryreceiveRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === user?.loginhistory?.companyId)?.inventoryreceive
      : null;

   const customerRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.customer
      : null;
   const vendorRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.vendor
      : null;
   const accountRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.AllAccount
      : null;
   const itemRight = useMemo(() => {
      if (!user?.userRights) return null;
      return Array.isArray(user.userRights)
         ? user.userRights.find((right) => right.companyId === companyId)?.item
         : null;
   }, [user?.userRights, companyId]);

   const { viewDetail, paginate } = useMemo(() => {
      let data = null;
      let pagination = null;

      const extractData = (sourceData) => {
         if (!sourceData) return null;
         if (sourceData.data && Array.isArray(sourceData.data) && sourceData.data.length > 0) {
            return sourceData.data[0];
         }
         if (sourceData.id || Object.keys(sourceData).length > 0) {
            return sourceData;
         }
         return null;
      };

      switch (entityType) {
         case 'saleinvoice':
            data = salesInvoiceData || extractData(currentViewDetail);
            pagination = 'Sales Invoice';
            break;
         case 'salereturn':
            data = extractData(salesReturnData) || extractData(currentViewDetail);
            pagination = 'Sales Return';
            break;
         case 'purchaseinvoice':
            data = purchaseInvoiceData || extractData(currentViewDetail);
            pagination = 'Purchase Invoice';
            break;
         case 'purchasereturn':
            data = extractData(purchaseReturnData) || extractData(currentViewDetail);
            pagination = 'Purchase Return';
            break;
         case 'issueinventory':
            data = extractData(issueInventoryData) || extractData(currentViewDetail);
            pagination = 'Issue';
            break;
         case 'receiveinventory':
            data = extractData(receiveInventoryData) || extractData(currentViewDetail);
            pagination = 'Receive';
            break;
         default:
            data = extractData(currentViewDetail);
            pagination = entityType;
      }

      return {
         viewDetail: data,
         paginate: pagination,
      };
   }, [
      entityType,
      currentViewDetail,
      salesInvoiceData,
      salesReturnData,
      purchaseInvoiceData,
      purchaseReturnData,
      issueInventoryData,
      receiveInventoryData,
   ]);

   useEffect(() => {
      const safeOpen = () => {
         if (!window.globalSearchModalOpen) {
            window.globalSearchModalOpen = true;
            onOpen();
         }
      };

      window.openGlobalSearchModal = safeOpen;

      return () => {
         if (window.openGlobalSearchModal === safeOpen) {
            window.openGlobalSearchModal = null;
         }
      };
   }, [onOpen]);

   useEffect(() => {
      return () => {
         if (!isOpen) {
            setTempRates({});
            setEditingItemId(null);
         }
      };
   }, [isOpen]);

   const handleClose = () => {
      window.globalSearchModalOpen = false;
      setDebouncedSearchQuery('');
      setIsSearchLoading(false);
      setTempRates({});
      setEditingItemId(null);
      onClose();
   };

   const handleSalesDetailsClose = () => {
      setIsOpenPurchaseReturnSingleView(false);
      setCurrentViewDetail(null);
      onOpen();
      setSearchQuery(tempSearch);
      debouncedSearchHandler(tempSearch);
      setTempSearch('');
   };

   const handleSerialNoForm = async (data) => {
      try {
         if (data.table_source === 'receive') {
            setEntityType('receiveinventory');
            const result = await dispatch(getReceiveInventory(data.id));
            setCurrentViewDetail(result);
            setIsOpenPurchaseReturnSingleView(true);
            setTempSearch(searchQuery);
            handleClose();
         }

         if (data.table_source === 'issue') {
            setEntityType('issueinventory');
            const result = await dispatch(getIssueInventory(data.id));
            setCurrentViewDetail(result);
            setIsOpenPurchaseReturnSingleView(true);
            setTempSearch(searchQuery);
            handleClose();
         }

         if (data.table_source === 'purchase') {
            setEntityType('purchaseinvoice');
            const result = await dispatch(getPurchaseInvoice(data.id));
            setCurrentViewDetail(result);
            setIsOpenPurchaseReturnSingleView(true);
            setTempSearch(searchQuery);
            handleClose();
         }

         if (data.table_source === 'sales') {
            setEntityType('saleinvoice');
            const result = await dispatch(getSaleInvoice(data.id));
            setCurrentViewDetail(result);
            setIsOpenPurchaseReturnSingleView(true);
            setTempSearch(searchQuery);
            handleClose();
         }

         if (data.table_source === 'purchase_return') {
            setEntityType('purchasereturn');
            const singleResult = await dispatch(getSinglePurchaseReturn(data.id));
            const paginatedResult = await dispatch(
               getSinglePurchaseReturnWithPrevAndNext({
                  id: data.id,
                  page: 1,
                  limit: 1,
                  sortBy: 'id',
                  sortOrder: 'desc',
               }),
            );
            setCurrentViewDetail(singleResult || paginatedResult);
            setIsOpenPurchaseReturnSingleView(true);
            setTempSearch(searchQuery);
            handleClose();
         }

         if (data.table_source === 'sales_return') {
            setEntityType('salereturn');
            const result = await dispatch(
               getSingleSalesReturnWithPrevAndNext({
                  id: data.id,
                  page: 1,
                  limit: 1,
                  sortBy: 'id',
                  sortOrder: 'desc',
               }),
            );
            setCurrentViewDetail(result);
            setIsOpenPurchaseReturnSingleView(true);
            setTempSearch(searchQuery);
            handleClose();
         }
      } catch (error) {
         console.error('Error handling serial form:', error);
      }
   };

   useEffect(() => {
      if (isOpenPurchaseReturnSingleView && viewDetail?.accountId) {
         dispatch(
            getAccountById({
               id: viewDetail.accountId,
               compId: user?.loginhistory?.companyId,
            }),
         );
      }
   }, [dispatch, isOpenPurchaseReturnSingleView, viewDetail?.accountId, user?.loginhistory?.companyId]);

   useEffect(() => {
      if (account) {
         const details = [
            { label: 'Bill To', value: account?.name || '-' },
            { label: 'Mobile', value: account?.mobile || '-' },
            { label: 'PAN', value: account?.panNo || '-' },
            { label: 'Billing Address', value: account?.address || '-' },
         ];
         setDynamicUserDetails(details);
      }
   }, [account]);

   const handleViewSingleData = async (id) => {
      try {
         const result = await dispatch(getReceiveInventory(id));
         setCurrentViewDetail(result);
         setIsOpenPurchaseReturnSingleView(true);
      } catch (error) {
         console.error('Error viewing single data:', error);
      }
   };

   const { userGlobalSettingsData } = useGlobal();

   const deletePurchaseReturn = () => {
      if (viewDetail?.id) {
         if (entityType === 'purchasereturn') {
            deleteRecord(
               viewDetail.id,
               deletePurchaseReturnInvoice,
               dispatch,
               user?.loginhistory?.companyId,
               userGlobalSettingsData,
            );
         }
         if (entityType === 'salereturn') {
            deleteRecord(
               viewDetail.id,
               deleteSaleReturnInvoice,
               dispatch,
               user?.loginhistory?.companyId,
               userGlobalSettingsData,
            );
         }
         if (entityType === 'saleinvoice') {
            deleteRecordForSales(
               viewDetail?.id,
               viewDetail.daybookId,
               viewDetail.accountId,
               deleteSaleInvoice,
               dispatch,
               user?.loginhistory?.companyId,
               userGlobalSettingsData,
            );
         }
         if (entityType === 'purchaseinvoice') {
            deleteRecordForSales(
               viewDetail?.id,
               viewDetail.daybookId,
               viewDetail.accountId,
               deletePurchaseInvoice,
               dispatch,
               user?.loginhistory?.companyId,
               userGlobalSettingsData,
            );
         }
         if (entityType === 'issueinventory') {
            deleteRecord(
               viewDetail.id,
               deleteIssueInventory,
               dispatch,
               user?.loginhistory?.companyId,
               userGlobalSettingsData,
            );
         }
         if (entityType === 'receiveinventory') {
            deleteRecord(
               viewDetail.id,
               deleteReceiveInventory,
               dispatch,
               user?.loginhistory?.companyId,
               userGlobalSettingsData,
            );
         }
      }
      setIsOpenPurchaseReturnSingleView(false);
      setCurrentViewDetail(null);
   };

   const handleReturnDetailsView = (item, event) => {
      event.stopPropagation();
      setSelectedReturnDetails({
         reason: item.reason || 'No reason provided',
         return_user: item.return_user || 'Unknown user',
         document_no: item.document_no,
         document_date: item.document_date,
         item_name: item.item_name,
         serial_no: item.serial_no,
         table_source: item.table_source,
         account_name: item.account_name,
      });
      setIsReturnDetailsOpen(true);
   };

   const handleBlockDetailsView = (item, event) => {
      event.stopPropagation();
      setSelectedBlockDetails({
         ...item,
         reason: item.blocked_reason || 'No reason provided',
         blocked_by: item.blocked_by || 'Unknown user',
         blocked_at: item.blocked_at || 'Unknown date',
         blockedByName: item.blockedByName || 'Unknown',
         blockedAt: item.blockedAt || item.blocked_at || 'Unknown date',
      });
      setIsBlockDetailsOpen(true);
   };

   const handleReturnDetailsClose = () => {
      setIsReturnDetailsOpen(false);
      setSelectedReturnDetails(null);
   };

   const handleBlockDetailsClose = () => {
      setIsBlockDetailsOpen(false);
      setSelectedBlockDetails(null);
   };

   useEffect(() => {
      const handleKeyDown = (event) => {
         if (event.key === 'Escape' && isOpen) {
            handleClose();
         }
         if (event.key === 'Escape' && isBlockDetailsOpen) {
            handleBlockDetailsClose();
         }
         if (event.key === 'Escape' && isReturnDetailsOpen) {
            handleReturnDetailsClose();
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [isOpen, isBlockDetailsOpen, isReturnDetailsOpen]);

   useEffect(() => {
      const handleEscAction = () => {
         if (isOpen) {
            handleClose();
         }
         if (isBlockDetailsOpen) {
            handleBlockDetailsClose();
         }
         if (isReturnDetailsOpen) {
            handleReturnDetailsClose();
         }
      };

      window.addEventListener('GLOBAL_ESC', handleEscAction);
      return () => window.removeEventListener('GLOBAL_ESC', handleEscAction);
   }, [isOpen, isBlockDetailsOpen, isReturnDetailsOpen]);

   const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
         handleClose();
      }
   };

   useEffect(() => {
      if (isOpen) {
         setTimeout(() => inputRef.current?.focus(), 100);
      } else {
         setSearchQuery('');
         setIsSearchLoading(false);
      }
   }, [isOpen]);

   useEffect(() => {
      if (searchQuery) {
         const results = [
            ...masterRoutes,
            ...purchaseRoutes,
            ...saleRoutes,
            ...otherRoutes,
            ...itemRoutes,
            ...transportRoutes,
            ...reportRoutes,
         ]
            .filter((route) => route.subName && route.subName.trim() !== '')
            .map((route) => ({
               name: `${route.subName} : ${route.name}`,
               path: `/admin${route.path}`,
               type: 'Menu',
            }))
            .filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()));

         setMenuResults(results);
      } else {
         setFilteredResults([]);
         setMenuResults([]);
      }
   }, [searchQuery]);

   useEffect(() => {
      if (searchresults) {
         const apiData = {
            ...searchresults,
            Menu: menuResults,
         };
         setFilteredResults(apiData);
         setIsSearchLoading(false);
      } else {
         setFilteredResults([]);
      }
   }, [searchresults, menuResults]);

   useEffect(() => {
      if (!isOpen && window.globalSearchModalOpen) {
         window.globalSearchModalOpen = false;
      }
   }, [isOpen]);

   const handleClick = () => {
      if (!window.globalSearchModalOpen) {
         window.globalSearchModalOpen = true;
         onOpen();
      }
   };

   const debounce = (func, delay) => {
      let timer;
      return function (...args) {
         clearTimeout(timer);
         timer = setTimeout(() => func.apply(this, args), delay);
      };
   };

   const debouncedSearchHandler = React.useCallback(
      debounce((query) => {
         setDebouncedSearchQuery(query);
      }, 1000),
      [],
   );

   const handleSearchChange = (value) => {
      setSearchQuery(value);
      if (value.trim()) {
         setIsSearchLoading(true);
      }
      debouncedSearchHandler(value);
   };

   const handleSearchClick = async (data, type) => {
      if (type === 'Menu') {
         handleClose();
         navigate(data.path);
      }

      if (type === 'accounts') {
         setEntityTypesv('Account');
         const singleData = await dispatch(getSingleCustomer({ id: data.id, compId: user?.loginhistory?.companyId }));
         var valueArray = [];
         valueArray = [
            { label: 'Name', value: singleData.name || '-' },
            { label: 'Email', value: singleData.email || '-' },
            { label: 'Mobile', value: singleData.mobile || '-' },
            {
               label: 'Address',
               value: singleData.accountaddress?.[0]?.address1 || '-',
            },
            { label: 'GSTIN', value: singleData.accountaddress?.[0]?.gst || '-' },
            { label: 'City', value: singleData.accountaddress?.[0]?.city || '-' },
            { label: 'State', value: singleData.accountaddress?.[0]?.state || '-' },
            {
               label: 'Country',
               value: singleData.accountaddress?.[0]?.country || '-',
            },
            {
               label: 'Pincode',
               value: singleData.accountaddress?.[0]?.pincode || '-',
            },
         ];
         setdynamicArray(valueArray);
         const today = new Date().toISOString().split('T')[0];
         const OS = await dispatch(
            getReportOutstandingDetail({
               accIds: data.id,
               fromDate: '2010-04-01',
               toDate: today,
               companyIds: String(user?.loginhistory?.companyId),
               isPurchaseOs: 1,
            }),
         );

         if (data) {
            const Ledger = await dispatch(
               getReportLedger({
                  fromDate: '2010-04-01',
                  toDate: today,
                  companyIds: user?.loginhistory?.companyId.toString(),
                  accIds: data.id.toString(),
               }),
            );

            setsingleEntityWithLedger({
               ...singleData,
               ledger: Ledger,
               os: OS,
            });
         }
         handleClose();
         setIsOpenSingleView(true);
      }

      if (type === 'items') {
         setEntityTypesv('item');
         const singleData = await dispatch(getSingleItem(data.id));
         var valueArray = [];
         valueArray = [
            { label: 'Name', value: singleData?.name || '-' },
            { label: 'Barcode', value: singleData?.barcode || '-' },
            { label: 'HSN', value: singleData?.hsn || '-' },
            { label: 'GSTIN', value: singleData?.gst || '-' },
            { label: 'Balance QTY 1', value: singleData?.stock || '-' },
            { label: 'Balance QTY 2', value: singleData?.balancequantity2 || '-' },
            { label: 'Balance QTY 3', value: singleData?.balancequantity3 || '-' },
         ];
         setdynamicArray(valueArray);
         setSingleItemData(singleData);
         handleClose();
         setIsOpenSingleView(true);
      }

      if (type === 'daybook') {
         if (data.type === 5) {
            handleClose();
            navigate('/admin/sales/invoice');
         } else if (data.type === 6) {
            handleClose();
            navigate('/admin/sales/return');
         } else if (data.type === 11) {
            handleClose();
            navigate('/admin/purchase/invoice');
         } else if (data.type === 12) {
            handleClose();
            navigate('/admin/purchase/return');
         } else if (data.type === 19) {
            handleClose();
            navigate('/admin/inventory/issue');
         } else if (data.type === 20) {
            handleClose();
            navigate('/admin/inventory/receive');
         } else if (data.type === 13) {
            handleClose();
            navigate('/admin/cash');
         } else if (data.type === 14) {
            handleClose();
            navigate('/admin/bank');
         }
      }
   };

   useEffect(() => {
      if (debouncedSearchQuery) {
         setFilteredResults([]);
         dispatch(
            getGlobalSearch({
               query: debouncedSearchQuery,
               companyId: user?.loginhistory?.companyId,
            }),
         );
      }
   }, [debouncedSearchQuery, dispatch, user?.loginhistory?.companyId]);

   const handleRefreshClick = async () => {
      if (!searchQuery.trim()) return;

      setIsRefreshing(true);
      setFilteredResults([]);
      setTempRates([]);

      try {
         await dispatch(
            getGlobalSearch({
               query: searchQuery,
               companyId: user?.loginhistory?.companyId,
            }),
         );
      } catch (error) {
         console.error('Refresh failed:', error);
      } finally {
         setIsRefreshing(false);
      }
   };

   const handleUnblock = async (blockData) => {
      const playload = {
         accountId: blockData?.account_id,
         salesIds: [blockData?.detail_id],
         isBlocked: false,
         type: blockData?.table_source === 'purchase_return' ? 'PURCHASE_RETURN' : 'SALES',
      };
      const res = await dispatch(partyServiceBlock(playload));
      if (res && res?.code === 1) {
         handleBlockDetailsClose();
         setFilteredResults([]);
         dispatch(
            getGlobalSearch({
               query: debouncedSearchQuery,
               companyId: user?.loginhistory?.companyId,
            }),
         );
      }
   };

   // Helper function to format blocked account details
   const formatBlockInfo = (item) => {
      const blockedAt = item.blockedAt || item.blocked_at;
      const blockedByName = item.blockedByName || 'Unknown';
      const reason = item.reason || item.blocked_reason || 'No reason provided';

      return {
         blockedAt: blockedAt
            ? new Date(blockedAt).toLocaleDateString('en-IN', {
               day: '2-digit',
               month: 'short',
               year: 'numeric',
               hour: '2-digit',
               minute: '2-digit',
            })
            : 'Unknown date',
         blockedByName,
         reason,
         fullDate: blockedAt ? new Date(blockedAt).toLocaleString('en-IN') : null,
      };
   };

   return (
      <>
         {/* Search Bar */}
         <Box
            position="relative"
            onClick={handleClick}
            role="button"
            cursor="pointer"
            _hover={{ transform: 'translateY(-2px)' }}
            transition="transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)">
            <InputGroup w={{ base: '100%', md: '280px', lg: '320px' }} {...rest}>
               <InputLeftElement pointerEvents="none" h="40px" pl="2px">
                  <Box
                     display="flex"
                     alignItems="center"
                     justifyContent="center"
                     w="32px"
                     h="32px"
                     borderRadius="full"
                     bg={isDark ? 'whiteAlpha.100' : '#F0F0F0'}
                     transition="all 0.3s">
                     <SearchIcon color={isDark ? 'blue.400' : 'blue.500'} w="14px" h="14px" />
                  </Box>
               </InputLeftElement>
               <Input
                  cursor="pointer"
                  variant="search"
                  h="40px"
                  fontSize="15px"
                  color={inputText}
                  fontWeight="500"
                  pl="52px"
                  pr="16px"
                  bg={
                     isDark
                        ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%)'
                        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.98) 100%)'
                  }
                  backdropFilter="blur(12px)"
                  border="1px solid"
                  borderColor={isDark ? 'whiteAlpha.200' : 'gray.300'}
                  borderRadius={borderRadius || 'full'}
                  boxShadow={
                     isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                        : '0 4px 20px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
                  }
                  _hover={{
                     borderColor: isDark ? 'blue.400' : 'blue.500',
                     bg: isDark ? 'linear-gradient(135deg, rgba(30, 41, 59, 1) 0%, rgba(15, 23, 42, 1) 100%)' : 'white',
                     boxShadow: isDark
                        ? '0 8px 32px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.3)'
                        : '0 8px 32px rgba(59, 130, 246, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.2)',
                  }}
                  _focus={{
                     borderColor: isDark ? 'blue.400' : 'blue.500',
                     bg: isDark ? 'linear-gradient(135deg, rgba(30, 41, 59, 1) 0%, rgba(15, 23, 42, 1) 100%)' : 'white',
                     boxShadow: isDark
                        ? '0 12px 40px rgba(59, 130, 246, 0.25), 0 0 0 2px rgba(59, 130, 246, 0.4)'
                        : '0 12px 40px rgba(59, 130, 246, 0.18), 0 0 0 2px rgba(59, 130, 246, 0.3)',
                     outline: 'none',
                  }}
                  _placeholder={{
                     color: isDark ? 'gray.500' : 'gray.400',
                     fontSize: '14px',
                     fontWeight: '400',
                  }}
                  placeholder="Search...."
                  readOnly
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
               />
            </InputGroup>

            {/* Decorative glow effect on hover */}
            <Box
               position="absolute"
               inset="-3px"
               borderRadius={borderRadius || 'full'}
               opacity="0"
               bg="linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2))"
               filter="blur(12px)"
               transition="opacity 0.3s"
               sx={{
                  '[role="button"]:hover &': {
                     opacity: 0.6,
                  },
               }}
               zIndex="-1"
               pointerEvents="none"
            />
         </Box>

         {/* Search Modal */}
         <Modal isOpen={isOpen} onClose={handleClose} size="6xl" closeOnEsc={true} onKeyDown={handleKeyDown}>
            <ModalOverlay bg={isDark ? 'blackAlpha.700' : 'blackAlpha.600'} backdropFilter="blur(8px)" />
            <ModalContent
               maxH="900px"
               maxW="1400px"
               className={`p-6 ${isDark ? 'bg-gray-900' : 'bg-white'} rounded-2xl shadow-2xl border ${isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
               <div className="flex items-center gap-2">
                  <InputGroup w="100%">
                     <InputLeftElement>
                        <SearchIcon color={searchIconColor} w="15px" h="15px" />
                     </InputLeftElement>
                     <Input
                        ref={inputRef}
                        variant="search"
                        fontSize="sm"
                        bg={background || inputBg}
                        color={inputText}
                        fontWeight="500"
                        borderRadius={borderRadius || '30px'}
                        _placeholder={{ color: 'gray.400', fontSize: '14px' }}
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        pl="40px"
                     />
                     <Box position="absolute" right="20px" top="50%" transform="translateY(-50%)" color="gray.400">
                        <HStack gap="1">
                           <Kbd>Esc</Kbd>
                        </HStack>
                     </Box>
                  </InputGroup>
                  <IconButton
                     aria-label="Refresh search"
                     icon={isRefreshing ? <Spinner size="sm" /> : <LuRefreshCcw />}
                     onClick={handleRefreshClick}
                     isDisabled={!searchQuery.trim()}
                     variant="ghost"
                     size="sm"
                     isLoading={isRefreshing}
                  />
               </div>

               {/* Search Results */}
               {searchQuery && (
                  <Box mt={3} width="100%" overflowY="auto" maxH="70vh">
                     {isSearchLoading ? (
                        // Show skeleton loaders while loading for ALL types
                        <>
                           {Object.entries(filteredResults).map(([type, items]) => (
                              <Box key={type} width="100%" mb={6}>
                                 <div
                                    className={`mb-3 pb-2 border-b-2 ${isDark ? 'border-indigo-500/50' : 'border-indigo-400'
                                       }`}>
                                    <Skeleton
                                       height="24px"
                                       width="120px"
                                       startColor={isDark ? 'gray.600' : 'gray.200'}
                                       endColor={isDark ? 'gray.700' : 'gray.300'}
                                    />
                                 </div>
                                 {type === 'serial_numbers' ? (
                                    <TableSkeleton />
                                 ) : (
                                    <List spacing={2}>
                                       {Array.from({ length: 3 }).map((_, index) => (
                                          <ListItem key={index}>
                                             <Box
                                                p={4}
                                                borderRadius="xl"
                                                border="2px"
                                                borderColor={isDark ? 'gray.700' : 'gray.200'}
                                                bg={isDark ? 'gray.800' : 'white'}>
                                                <HStack spacing={3} align="start">
                                                   <SkeletonCircle
                                                      size="32px"
                                                      startColor={isDark ? 'gray.600' : 'gray.200'}
                                                      endColor={isDark ? 'gray.700' : 'gray.300'}
                                                   />
                                                   <Box flex={1}>
                                                      <Skeleton
                                                         height="16px"
                                                         width="70%"
                                                         mb={2}
                                                         startColor={isDark ? 'gray.600' : 'gray.200'}
                                                         endColor={isDark ? 'gray.700' : 'gray.300'}
                                                      />
                                                      <Skeleton
                                                         height="14px"
                                                         width="90%"
                                                         startColor={isDark ? 'gray.600' : 'gray.200'}
                                                         endColor={isDark ? 'gray.700' : 'gray.300'}
                                                      />
                                                   </Box>
                                                </HStack>
                                             </Box>
                                          </ListItem>
                                       ))}
                                    </List>
                                 )}
                              </Box>
                           ))}
                           {/* If no filteredResults yet, show default skeletons */}
                           {Object.keys(filteredResults).length === 0 && (
                              <>
                                 <SearchResultSkeleton />
                                 <SearchResultSkeleton />
                                 <TableSkeleton />
                              </>
                           )}
                        </>
                     ) : (
                        // When not loading, show actual results or no results state
                        <>
                           {Object.keys(filteredResults).length > 0 &&
                              Object.values(filteredResults).some((items) => Array.isArray(items) && items.length > 0) ? (
                              Object.entries(filteredResults).map(
                                 ([type, items]) =>
                                    Array.isArray(items) &&
                                    items.length > 0 && (
                                       <Box key={type} width="100%" mb={6}>
                                          <div
                                             className={`mb-3 pb-2 border-b-2 ${isDark ? 'border-indigo-500/50' : 'border-indigo-400'
                                                }`}>
                                             <Text
                                                fontWeight="700"
                                                fontSize="lg"
                                                color={isDark ? '#a5b4fc' : '#4f46e5'}
                                                textTransform="uppercase"
                                                letterSpacing="wide">
                                                {type}
                                             </Text>
                                          </div>
                                          <List spacing={2}>
                                             {type === 'serial_numbers' ? (
                                                // ACTUAL SERIAL NUMBERS TABLE
                                                <div
                                                   className={`overflow-hidden rounded-xl border-2 ${isDark ? 'border-gray-700' : 'border-gray-200'
                                                      } shadow-lg`}>
                                                   <Table variant="simple" size="sm">
                                                      <Thead
                                                         className={
                                                            isDark
                                                               ? 'bg-gradient-to-r from-gray-800 to-gray-750'
                                                               : 'bg-gradient-to-r from-gray-50 to-gray-100'
                                                         }>
                                                         <Tr>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Date
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Doc No
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Account Name
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Mobile No
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Item Name
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Daybook
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Warehous Name
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Serial No
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Type
                                                            </Th>
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Item Rate
                                                            </Th>
                                                            {(user?.isAdmin || user?.isShowDealerPrice) && (
                                                               <Th
                                                                  className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                        ? 'text-gray-300 border-gray-700'
                                                                        : 'text-gray-700 border-gray-200'
                                                                     }`}>
                                                                  Dealer Price
                                                               </Th>
                                                            )}
                                                            {(user?.isAdmin || user?.isShowSalesPrice) && (
                                                               <Th
                                                                  className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                        ? 'text-gray-300 border-gray-700'
                                                                        : 'text-gray-700 border-gray-200'
                                                                     }`}>
                                                                  Sales Price
                                                               </Th>
                                                            )}
                                                            <Th
                                                               className={`py-4 px-4 font-bold text-xs uppercase tracking-wider ${isDark
                                                                     ? 'text-gray-300 border-gray-700'
                                                                     : 'text-gray-700 border-gray-200'
                                                                  }`}>
                                                               Action
                                                            </Th>
                                                         </Tr>
                                                      </Thead>
                                                      <Tbody>
                                                         {items.map((item) => {
                                                            const isReturnType =
                                                               item.table_source === 'issue_return' ||
                                                               item.table_source === 'receive_return';

                                                            const isBlocked = item.is_blocked === true;

                                                            return (
                                                               <Tr
                                                                  key={item.id}
                                                                  className={`cursor-pointer transition-all duration-200 border-l-4 
                                                                     ${isBlocked
                                                                        ? isDark
                                                                           ? 'bg-red-900/40 border-l-red-600'
                                                                           : 'bg-red-100 border-l-red-500'
                                                                        : isReturnType
                                                                           ? isDark
                                                                              ? 'bg-red-950/30 border-l-red-500 hover:bg-red-900/40'
                                                                              : 'bg-red-50 border-l-red-400 hover:bg-red-100'
                                                                           : isDark
                                                                              ? 'border-l-transparent hover:bg-gray-800/50'
                                                                              : 'border-l-transparent hover:bg-blue-50'
                                                                     }
                                                                  `}
                                                                  onClick={(e) => {
                                                                     if (
                                                                        item.table_source !== 'purchase' ||
                                                                        !e.target.closest('.rate-edit-cell')
                                                                     ) {
                                                                        handleSerialNoForm(item);
                                                                     }
                                                                  }}>
                                                                  <Td
                                                                     className={`py-3 px-4 ${isDark
                                                                           ? 'text-gray-300 border-gray-700'
                                                                           : 'text-gray-700 border-gray-200'
                                                                        }`}>
                                                                     {item.document_date
                                                                        ? new Date(
                                                                           item.document_date,
                                                                        ).toLocaleDateString('en-GB')
                                                                        : '-'}
                                                                  </Td>
                                                                  <Td
                                                                     className={`py-3 px-4 font-semibold ${isBlocked
                                                                           ? isDark
                                                                              ? 'text-red-400'
                                                                              : 'text-red-600'
                                                                           : isReturnType
                                                                              ? isDark
                                                                                 ? 'text-red-400'
                                                                                 : 'text-red-600'
                                                                              : isDark
                                                                                 ? 'text-indigo-400'
                                                                                 : 'text-indigo-600'
                                                                        } ${isDark ? 'border-gray-700' : 'border-gray-200'
                                                                        }`}>
                                                                     {item.document_no}
                                                                  </Td>
                                                                  <Td
                                                                     className={`py-3 px-4 ${isDark
                                                                           ? 'text-gray-300 border-gray-700'
                                                                           : 'text-gray-700 border-gray-200'
                                                                        }`}>
                                                                     {item.account_name}
                                                                  </Td>
                                                                  <Td
                                                                     className={`py-3 px-4 ${isDark
                                                                           ? 'text-gray-400 border-gray-700'
                                                                           : 'text-gray-600 border-gray-200'
                                                                        }`}>
                                                                     {item.account_mobile}
                                                                  </Td>
                                                                  <Td
                                                                     className={`py-3 px-4 ${isDark
                                                                           ? 'text-gray-300 border-gray-700'
                                                                           : 'text-gray-700 border-gray-200'
                                                                        }`}>
                                                                     {item.item_name}
                                                                  </Td>
                                                                  <Td
                                                                     className={`py-3 px-4 ${isDark
                                                                           ? 'text-gray-400 border-gray-700'
                                                                           : 'text-gray-600 border-gray-200'
                                                                        }`}>
                                                                     {item.daybook_name}
                                                                  </Td>
                                                                  <Td
                                                                     className={`py-3 px-4 ${isDark
                                                                           ? 'text-gray-400 border-gray-700'
                                                                           : 'text-gray-600 border-gray-200'
                                                                        }`}>
                                                                     {item.warehouse_name}
                                                                  </Td>
                                                                  <Td
                                                                     className={`py-3 px-4 font-mono ${isDark
                                                                           ? 'text-cyan-400 border-gray-700'
                                                                           : 'text-cyan-600 border-gray-200'
                                                                        }`}>
                                                                     {item.serial_no}
                                                                  </Td>
                                                                  <Td
                                                                     className={`py-3 px-4 ${isDark ? 'border-gray-700' : 'border-gray-200'
                                                                        }`}>
                                                                     <span
                                                                        className={`px-3 py-1 rounded-full text-xs font-semibold ${isBlocked
                                                                              ? isDark
                                                                                 ? 'bg-red-900/50 text-red-300 border border-red-700'
                                                                                 : 'bg-red-100 text-red-700 border border-red-300'
                                                                              : isReturnType
                                                                                 ? isDark
                                                                                    ? 'bg-red-900/50 text-red-300 border border-red-700'
                                                                                    : 'bg-red-100 text-red-700 border border-red-300'
                                                                                 : isDark
                                                                                    ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-700'
                                                                                    : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                                                           }`}>
                                                                        {item.table_source}
                                                                     </span>
                                                                  </Td>
                                                                  {/* <Td
                                                                     className={`py-3 px-4 font-semibold ${
                                                                        isDark
                                                                           ? 'text-green-400 border-gray-700'
                                                                           : 'text-green-600 border-gray-200'
                                                                     }`}>
                                                                     ₹{item.item_rate}
                                                                  </Td> */}
                                                                  <Td
                                                                     className={`py-3 px-4 rate-edit-cell ${isDark ? 'border-gray-700' : 'border-gray-200'
                                                                        }`}
                                                                     onClick={(e) => e.stopPropagation()}>
                                                                     {item.table_source === 'purchase' ? (
                                                                        <div className="flex items-center gap-2">
                                                                           {editingItemId === item.id ? (
                                                                              <div className="flex items-center gap-2">
                                                                                 <Input
                                                                                    size="sm"
                                                                                    style={{ width: '80px' }}
                                                                                    value={
                                                                                       tempRates[item.id] !== undefined
                                                                                          ? tempRates[item.id]
                                                                                          : item.item_rate
                                                                                    }
                                                                                    onChange={(e) => {
                                                                                       const value = e.target.value;
                                                                                       // Allow empty string (for backspace), numbers and decimal
                                                                                       if (
                                                                                          value === '' ||
                                                                                          /^\d*\.?\d*$/.test(value)
                                                                                       ) {
                                                                                          setTempRates((prev) => ({
                                                                                             ...prev,
                                                                                             [item.id]: value,
                                                                                          }));
                                                                                       }
                                                                                    }}
                                                                                    onKeyDown={(e) => {
                                                                                       if (e.key === 'Enter') {
                                                                                          setEditingItemId(null);
                                                                                       }
                                                                                       if (e.key === 'Escape') {
                                                                                          setTempRates((prev) => {
                                                                                             const newRates = {
                                                                                                ...prev,
                                                                                             };
                                                                                             delete newRates[item.id];
                                                                                             return newRates;
                                                                                          });
                                                                                          setEditingItemId(null);
                                                                                       }
                                                                                    }}
                                                                                    onBlur={() => {
                                                                                       // If input is empty, restore original value
                                                                                       if (
                                                                                          tempRates[item.id] === '' ||
                                                                                          tempRates[item.id] ===
                                                                                          undefined
                                                                                       ) {
                                                                                          setTempRates((prev) => {
                                                                                             const newRates = {
                                                                                                ...prev,
                                                                                             };
                                                                                             delete newRates[item.id];
                                                                                             return newRates;
                                                                                          });
                                                                                       }
                                                                                       setEditingItemId(null);
                                                                                    }}
                                                                                    className={`${isDark
                                                                                          ? 'bg-gray-800 text-gray-100 border-gray-600'
                                                                                          : 'bg-white text-gray-800 border-gray-300'
                                                                                       }`}
                                                                                    autoFocus
                                                                                 />
                                                                              </div>
                                                                           ) : (
                                                                              <div className="flex items-center">
                                                                                 <span
                                                                                    className={`font-semibold cursor-pointer hover:underline ${isDark
                                                                                          ? 'text-green-400'
                                                                                          : 'text-green-600'
                                                                                       }`}
                                                                                    onClick={(e) => {
                                                                                       e.stopPropagation();
                                                                                       setEditingItemId(item.id);
                                                                                    }}>
                                                                                    ₹
                                                                                    {tempRates[item.id] !== undefined
                                                                                       ? tempRates[item.id]
                                                                                       : item.item_rate}
                                                                                 </span>
                                                                              </div>
                                                                           )}
                                                                        </div>
                                                                     ) : (
                                                                        <span
                                                                           className={`font-semibold ${isDark
                                                                                 ? 'text-green-400'
                                                                                 : 'text-green-600'
                                                                              }`}>
                                                                           ₹{item.item_rate}
                                                                        </span>
                                                                     )}
                                                                  </Td>
                                                                  {(user?.isAdmin || user?.isShowDealerPrice) && (
                                                                     <Td
                                                                        className={`py-3 px-4 font-semibold ${isDark
                                                                              ? 'text-green-400 border-gray-700'
                                                                              : 'text-green-600 border-gray-200'
                                                                           }`}>
                                                                        {(item.table_source === 'purchase' ||
                                                                           item.table_source === 'receive') &&
                                                                           item.dealer_price
                                                                           ? '₹' + item.dealer_price
                                                                           : '-'}
                                                                     </Td>
                                                                  )}
                                                                  {(user?.isAdmin || user?.isShowSalesPrice) && (
                                                                     <Td
                                                                        className={`py-3 px-4 font-semibold ${isDark
                                                                              ? 'text-green-400 border-gray-700'
                                                                              : 'text-green-600 border-gray-200'
                                                                           }`}>
                                                                        {(item.table_source === 'purchase' ||
                                                                           item.table_source === 'receive') &&
                                                                           item.sales_price
                                                                           ? '₹' + item.sales_price
                                                                           : '-'}
                                                                     </Td>
                                                                  )}
                                                                  <Td
                                                                     className={`py-3 px-4 ${isDark ? 'border-gray-700' : 'border-gray-200'
                                                                        }`}>
                                                                     {(isReturnType || isBlocked) && (
                                                                        <IconButton
                                                                           icon={<ViewIcon />}
                                                                           size="sm"
                                                                           className={`rounded-lg transition-all ${isDark
                                                                                 ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800 border border-blue-700'
                                                                                 : 'bg-blue-100 text-blue-600 hover:bg-blue-200 border border-blue-300'
                                                                              }`}
                                                                           aria-label="View return details"
                                                                           onClick={(e) => {
                                                                              if (isBlocked) {
                                                                                 handleBlockDetailsView(item, e);
                                                                              } else {
                                                                                 handleReturnDetailsView(item, e);
                                                                              }
                                                                           }}
                                                                        />
                                                                     )}
                                                                  </Td>
                                                               </Tr>
                                                            );
                                                         })}
                                                      </Tbody>
                                                   </Table>
                                                </div>
                                             ) : (
                                                items.map((item, index) => {
                                                   const isAccountBlocked = type === 'accounts' && item.isBlacklisted;
                                                   const blockInfo = isAccountBlocked ? formatBlockInfo(item) : null;

                                                   return (
                                                      <ListItem
                                                         key={index}
                                                         className={`p-2 rounded-xl border-2 cursor-pointer transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-1 ${isAccountBlocked
                                                               ? isDark
                                                                  ? 'bg-gradient-to-br from-red-950/40 to-red-900/30 border-red-700 hover:border-red-500 hover:bg-red-900/40'
                                                                  : 'bg-gradient-to-br from-red-50 to-red-100 border-red-300 hover:border-red-400 hover:bg-red-200'
                                                               : isDark
                                                                  ? 'bg-gradient-to-br from-gray-800 to-gray-750 border-gray-700 hover:border-indigo-500 hover:bg-gray-700'
                                                                  : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 hover:border-indigo-400 hover:bg-blue-50'
                                                            }`}
                                                         onClick={() => handleSearchClick(item, type)}>
                                                         {type === 'accounts' ? (
                                                            <div className="space-y-2">
                                                               <Flex
                                                                  justify="space-between"
                                                                  align="center"
                                                                  className="mb-2">
                                                                  <div
                                                                     className={`flex items-center gap-3 px-4 py-2 rounded-lg border-l-4 ${isAccountBlocked
                                                                           ? isDark
                                                                              ? 'bg-red-950/50 border-l-red-500'
                                                                              : 'bg-red-100 border-l-red-500'
                                                                           : isDark
                                                                              ? 'bg-indigo-950/30 border-l-indigo-500'
                                                                              : 'bg-indigo-50 border-l-indigo-500'
                                                                        }`}>
                                                                     <div className="flex items-center gap-3">
                                                                        <div
                                                                           className={`relative ${isAccountBlocked ? 'opacity-90' : ''
                                                                              }`}>
                                                                           <div
                                                                              className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-lg ${isAccountBlocked
                                                                                    ? isDark
                                                                                       ? 'bg-red-900/50 text-red-300 border-2 border-red-700'
                                                                                       : 'bg-red-200 text-red-700 border-2 border-red-500'
                                                                                    : isDark
                                                                                       ? 'bg-indigo-900/50 text-indigo-300 border-2 border-indigo-700'
                                                                                       : 'bg-indigo-200 text-indigo-700 border-2 border-indigo-400'
                                                                                 }`}>
                                                                              {item.name?.charAt(0).toUpperCase()}
                                                                           </div>
                                                                           {isAccountBlocked && (
                                                                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center">
                                                                                 <svg
                                                                                    className="w-3 h-3 text-white"
                                                                                    fill="currentColor"
                                                                                    viewBox="0 0 20 20"
                                                                                    xmlns="http://www.w3.org/2000/svg">
                                                                                    <path
                                                                                       fillRule="evenodd"
                                                                                       d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z"
                                                                                       clipRule="evenodd"
                                                                                    />
                                                                                 </svg>
                                                                              </div>
                                                                           )}
                                                                        </div>
                                                                        <div>
                                                                           <div className="flex items-center gap-2">
                                                                              <Text
                                                                                 fontWeight="bold"
                                                                                 fontSize="14px"
                                                                                 className={
                                                                                    isAccountBlocked
                                                                                       ? isDark
                                                                                          ? 'text-red-300'
                                                                                          : 'text-red-700'
                                                                                       : isDark
                                                                                          ? 'text-gray-100'
                                                                                          : 'text-gray-900'
                                                                                 }>
                                                                                 {item.name}
                                                                              </Text>
                                                                              {isAccountBlocked && (
                                                                                 <Badge
                                                                                    className={`px-2 py-0.5 text-xs font-bold rounded-full ${isDark
                                                                                          ? 'bg-red-900/60 text-red-200 border border-red-700'
                                                                                          : 'bg-red-200 text-red-800 border border-red-400'
                                                                                       }`}>
                                                                                    BLOCKED
                                                                                 </Badge>
                                                                              )}
                                                                           </div>
                                                                           {isAccountBlocked && blockInfo && (
                                                                              <Tooltip
                                                                                 label={`Blocked by: ${blockInfo.blockedByName}\nReason: ${blockInfo.reason}\nDate: ${blockInfo.blockedAt}`}
                                                                                 placement="bottom"
                                                                                 hasArrow>
                                                                                 <Text
                                                                                    fontSize="xs"
                                                                                    className={`mt-1 flex items-center gap-1 ${isDark
                                                                                          ? 'text-red-400'
                                                                                          : 'text-red-600'
                                                                                       }`}>
                                                                                    <svg
                                                                                       className="w-3 h-3"
                                                                                       fill="currentColor"
                                                                                       viewBox="0 0 20 20"
                                                                                       xmlns="http://www.w3.org/2000/svg">
                                                                                       <path
                                                                                          fillRule="evenodd"
                                                                                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                                                                          clipRule="evenodd"
                                                                                       />
                                                                                    </svg>
                                                                                    Blocked on {blockInfo.blockedAt}
                                                                                 </Text>
                                                                              </Tooltip>
                                                                           )}
                                                                        </div>
                                                                     </div>
                                                                  </div>
                                                                  {item.closing_balance !== undefined &&
                                                                     item.closing_balance !== 0 && (
                                                                        <div
                                                                           className={`px-4 py-2 rounded-lg font-bold text-sm border-2 ${item.closing_balance > 0
                                                                                 ? isAccountBlocked
                                                                                    ? isDark
                                                                                       ? 'bg-red-950/40 text-red-300 border-red-600'
                                                                                       : 'bg-red-100 text-red-700 border-red-400'
                                                                                    : isDark
                                                                                       ? 'bg-red-950/30 text-red-400 border-red-700'
                                                                                       : 'bg-red-100 text-red-600 border-red-300'
                                                                                 : isAccountBlocked
                                                                                    ? isDark
                                                                                       ? 'bg-green-950/40 text-green-300 border-green-600'
                                                                                       : 'bg-green-100 text-green-700 border-green-400'
                                                                                    : isDark
                                                                                       ? 'bg-green-950/30 text-green-400 border-green-700'
                                                                                       : 'bg-green-100 text-green-600 border-green-300'
                                                                              }`}>
                                                                           {Math.abs(
                                                                              item.closing_balance,
                                                                           ).toLocaleString('en-IN', {
                                                                              style: 'currency',
                                                                              currency: 'INR',
                                                                           })}{' '}
                                                                           {item.closing_balance > 0 ? 'CR' : 'DB'}
                                                                        </div>
                                                                     )}
                                                               </Flex>

                                                               {(item.gstRegNo || item.mobile || item.panNo) && (
                                                                  <div
                                                                     className={`p-2 rounded-lg border ${isAccountBlocked
                                                                           ? isDark
                                                                              ? 'bg-red-950/20 border-red-800'
                                                                              : 'bg-red-50/80 border-red-200'
                                                                           : isDark
                                                                              ? 'bg-gray-850/50 border-gray-700'
                                                                              : 'bg-gray-50 border-gray-200'
                                                                        }`}>
                                                                     <Text
                                                                        fontSize="sm"
                                                                        className={`flex flex-wrap gap-x-4 gap-y-1 ${isAccountBlocked
                                                                              ? isDark
                                                                                 ? 'text-red-300'
                                                                                 : 'text-red-600'
                                                                              : isDark
                                                                                 ? 'text-gray-400'
                                                                                 : 'text-gray-600'
                                                                           }`}>
                                                                        {item?.gstRegNo && (
                                                                           <span className="flex items-center gap-1">
                                                                              <span className="font-semibold">
                                                                                 GST:
                                                                              </span>
                                                                              <span
                                                                                 className={
                                                                                    isAccountBlocked
                                                                                       ? isDark
                                                                                          ? 'text-cyan-300'
                                                                                          : 'text-cyan-700'
                                                                                       : isDark
                                                                                          ? 'text-cyan-400'
                                                                                          : 'text-cyan-600'
                                                                                 }>
                                                                                 {item.gstRegNo}
                                                                              </span>
                                                                           </span>
                                                                        )}
                                                                        {item?.panNo && (
                                                                           <span className="flex items-center gap-1">
                                                                              <span className="font-semibold">
                                                                                 PAN:
                                                                              </span>
                                                                              <span
                                                                                 className={
                                                                                    isAccountBlocked
                                                                                       ? isDark
                                                                                          ? 'text-yellow-300'
                                                                                          : 'text-yellow-700'
                                                                                       : isDark
                                                                                          ? 'text-yellow-400'
                                                                                          : 'text-yellow-600'
                                                                                 }>
                                                                                 {item.panNo}
                                                                              </span>
                                                                           </span>
                                                                        )}
                                                                        {item?.mobile && (
                                                                           <span className="flex items-center gap-1">
                                                                              <span className="font-semibold">
                                                                                 Mobile:
                                                                              </span>
                                                                              <span
                                                                                 className={
                                                                                    isAccountBlocked
                                                                                       ? isDark
                                                                                          ? 'text-green-300'
                                                                                          : 'text-green-700'
                                                                                       : isDark
                                                                                          ? 'text-green-400'
                                                                                          : 'text-green-600'
                                                                                 }>
                                                                                 {item.mobile}
                                                                              </span>
                                                                           </span>
                                                                        )}
                                                                     </Text>
                                                                  </div>
                                                               )}
                                                            </div>
                                                         ) : (
                                                            <Flex justify="space-between" align="center">
                                                               <Text
                                                                  fontWeight="medium"
                                                                  className={
                                                                     isDark ? 'text-gray-200' : 'text-gray-800'
                                                                  }>
                                                                  {item.name}
                                                               </Text>
                                                               <Badge
                                                                  px={3}
                                                                  py={1}
                                                                  borderRadius="md"
                                                                  fontSize="0.8rem"
                                                                  colorScheme={
                                                                     item.closing_stock > 0 ? 'green' : 'red'
                                                                  }>
                                                                  {item.closing_stock}
                                                               </Badge>
                                                            </Flex>
                                                         )}
                                                      </ListItem>
                                                   );
                                                })
                                             )}
                                          </List>
                                       </Box>
                                    ),
                              )
                           ) : (
                              // Show no results state when there are no results
                              <Box textAlign="center" mt={8}>
                                 <Image
                                    src={nofound}
                                    alt="No results found"
                                    width="300px"
                                    height="300px"
                                    mx="auto"
                                    mb={5}
                                 />
                                 <Text fontSize="xl" fontWeight="bold" color={isDark ? 'gray.300' : 'gray.600'} mb={2}>
                                    No results found
                                 </Text>
                                 <Text fontSize="md" color={isDark ? 'gray.400' : 'gray.500'}>
                                    Try searching with different keywords
                                 </Text>
                              </Box>
                           )}
                        </>
                     )}
                  </Box>
               )}
            </ModalContent>
         </Modal>

         {/* Return Details Modal */}
         <Modal isOpen={isReturnDetailsOpen} onClose={handleReturnDetailsClose} size="lg">
            <ModalOverlay bg={isDark ? 'blackAlpha.700' : 'blackAlpha.600'} backdropFilter="blur(8px)" />
            <ModalContent
               className={`p-6 ${isDark ? 'bg-gray-900 border-2 border-gray-700' : 'bg-white border-2 border-gray-200'
                  } rounded-2xl shadow-2xl`}>
               <ModalCloseButton
                  className={isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}
               />
               <VStack align="start" spacing={5} className="mt-2">
                  <div className="w-full">
                     <div
                        className={`flex items-center gap-3 mb-4 pb-3 border-b-2 ${isDark ? 'border-red-700' : 'border-red-400'
                           }`}>
                        <div
                           className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-red-950/50 border-2 border-red-700' : 'bg-red-100 border-2 border-red-400'
                              }`}>
                           <ViewIcon className={`w-6 h-6 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                        </div>
                        <Text fontSize="2xl" fontWeight="bold" className={isDark ? 'text-gray-100' : 'text-gray-900'}>
                           Return Details
                        </Text>
                     </div>

                     {selectedReturnDetails && (
                        <VStack align="start" spacing={4} w="100%">
                           <div
                              className={`w-full p-4 rounded-xl border-2 ${isDark ? 'bg-red-950/20 border-red-800' : 'bg-red-50 border-red-200'
                                 }`}>
                              <div className="space-y-3">
                                 <div
                                    className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                       }`}>
                                    <Text
                                       fontSize="xs"
                                       fontWeight="semibold"
                                       className={`uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'
                                          }`}>
                                       Return User
                                    </Text>
                                    <Text
                                       fontSize="md"
                                       fontWeight="bold"
                                       className={isDark ? 'text-red-400' : 'text-red-600'}>
                                       {selectedReturnDetails.return_user}
                                    </Text>
                                 </div>

                                 <div
                                    className={`p-4 rounded-lg border-2 ${isDark ? 'bg-gray-850 border-red-800' : 'bg-white border-red-300'
                                       }`}>
                                    <Text
                                       fontSize="xs"
                                       fontWeight="semibold"
                                       className={`uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-500'
                                          }`}>
                                       Return Reason
                                    </Text>
                                    <div
                                       className={`p-3 rounded-md ${isDark
                                             ? 'bg-red-950/30 border border-red-800'
                                             : 'bg-red-50 border border-red-200'
                                          }`}>
                                       <Text
                                          fontSize="sm"
                                          className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                          {selectedReturnDetails.reason}
                                       </Text>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </VStack>
                     )}
                  </div>
               </VStack>
            </ModalContent>
         </Modal>

         {/* Blocked Account Details Modal */}
         <Modal isOpen={isBlockDetailsOpen} onClose={handleBlockDetailsClose} size="lg">
            <ModalOverlay bg={isDark ? 'blackAlpha.700' : 'blackAlpha.600'} backdropFilter="blur(8px)" />
            <ModalContent
               className={`p-6 ${isDark ? 'bg-gray-900 border-2 border-gray-700' : 'bg-white border-2 border-gray-200'
                  } rounded-2xl shadow-2xl`}>
               <ModalCloseButton
                  className={isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}
               />
               <VStack align="start" spacing={5} className="mt-2">
                  <div className="w-full">
                     <div
                        className={`flex items-center gap-3 mb-4 pb-3 border-b-2 ${isDark ? 'border-red-700' : 'border-red-400'
                           }`}>
                        <div
                           className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-red-950/50 border-2 border-red-700' : 'bg-red-100 border-2 border-red-400'
                              }`}>
                           <Ban className={`w-7 h-7 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                        </div>

                        <Text fontSize="2xl" fontWeight="bold" className={isDark ? 'text-gray-100' : 'text-gray-900'}>
                           Account Blocked Details
                        </Text>
                     </div>

                     {/* Details Section */}
                     {selectedBlockDetails && (
                        <VStack align="start" spacing={4} w="100%">
                           <div
                              className={`w-full p-4 rounded-xl border-2 ${isDark ? 'bg-red-950/20 border-red-800' : 'bg-red-50 border-red-200'
                                 }`}>
                              <div className="space-y-4">
                                 {/* Blocked By Name */}
                                 <div
                                    className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                       }`}>
                                    <Text
                                       fontSize="xs"
                                       fontWeight="semibold"
                                       className={`uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'
                                          }`}>
                                       Blocked By
                                    </Text>
                                    <div className="flex items-center gap-2">
                                       <div
                                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-200 text-red-700'
                                             }`}>
                                          {selectedBlockDetails.blocked_by?.charAt(0) || 'U'}
                                       </div>
                                       <Text
                                          fontSize="md"
                                          fontWeight="bold"
                                          className={isDark ? 'text-red-400' : 'text-red-600'}>
                                          {selectedBlockDetails.blocked_by || 'Unknown'}
                                       </Text>
                                    </div>
                                 </div>

                                 {/* Blocked At Date */}
                                 <div
                                    className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                       }`}>
                                    <Text
                                       fontSize="xs"
                                       fontWeight="semibold"
                                       className={`uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'
                                          }`}>
                                       Blocked At
                                    </Text>
                                    <div className="flex items-center gap-2">
                                       <svg
                                          className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-600'}`}
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                          xmlns="http://www.w3.org/2000/svg">
                                          <path
                                             fillRule="evenodd"
                                             d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                             clipRule="evenodd"
                                          />
                                       </svg>
                                       <Text
                                          fontSize="md"
                                          fontWeight="bold"
                                          className={isDark ? 'text-gray-300' : 'text-gray-800'}>
                                          {selectedBlockDetails.blockedAt
                                             ? new Date(selectedBlockDetails.blockedAt).toLocaleString('en-IN', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                             })
                                             : '-'}
                                       </Text>
                                    </div>
                                 </div>

                                 {/* Block Reason */}
                                 <div
                                    className={`p-4 rounded-lg border-2 ${isDark ? 'bg-gray-850 border-red-800' : 'bg-white border-red-300'
                                       }`}>
                                    <div className="flex items-center gap-2 mb-3">
                                       <svg
                                          className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`}
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                          xmlns="http://www.w3.org/2000/svg">
                                          <path
                                             fillRule="evenodd"
                                             d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                             clipRule="evenodd"
                                          />
                                       </svg>
                                       <Text
                                          fontSize="xs"
                                          fontWeight="semibold"
                                          className={`uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'
                                             }`}>
                                          Reason for Blocking
                                       </Text>
                                    </div>

                                    <div
                                       className={`p-3 rounded-md ${isDark
                                             ? 'bg-red-950/30 border border-red-800'
                                             : 'bg-red-50 border border-red-200'
                                          }`}>
                                       <Text
                                          fontSize="sm"
                                          className={`leading-relaxed font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'
                                             }`}>
                                          {selectedBlockDetails.reason}
                                       </Text>
                                    </div>
                                 </div>

                                 {/* Additional Info */}
                                 {selectedBlockDetails.fullDate && (
                                    <div
                                       className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                                          }`}>
                                       <Text
                                          fontSize="xs"
                                          className={`italic ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                          Full date: {selectedBlockDetails.fullDate}
                                       </Text>
                                    </div>
                                 )}
                              </div>
                              <button
                                 onClick={() => handleUnblock(selectedBlockDetails)}
                                 className={`w-full mt-5 flex-1 py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg flex items-center justify-center gap-2 ${isDark
                                       ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500'
                                       : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                                    }`}>
                                 Unblocking
                              </button>
                           </div>
                        </VStack>
                     )}
                  </div>
               </VStack>
            </ModalContent>
         </Modal>

         <ViewSingleData
            singleEntityRights={customerRight || vendorRight || accountRight || itemRight}
            isOpenSingleView={isOpenSingleView}
            onClose={() => setIsOpenSingleView(false)}
            entityType={entityTypesv === 'Account' ? 'Account' : 'item'}
            singleEntity={entityTypesv === 'Account' ? singleEntityWithLedger : singleItemData}
            dynamicArray={dynamicArray}
            ItemId={entityTypesv === 'Account' ? singleEntityWithLedger?.id : singleItemData?.id}
            formType={entityTypesv === 'Account' ? 'Account' : 'Item'}
         />

         <SalesDetails
            isOpen={isOpenPurchaseReturnSingleView}
            onClose={handleSalesDetailsClose}
            singleEntityRights={
               saleInvoiceRight ||
               saleReturnRight ||
               purchaseInvoiceRight ||
               purchaseReturnRight ||
               inventoryissueRight ||
               inventoryreceiveRight
            }
            title={
               entityType === 'saleinvoice'
                  ? 'Sales Invoice'
                  : entityType === 'salereturn'
                     ? 'Sales Return'
                     : entityType === 'purchaseinvoice'
                        ? 'Purchase Invoice'
                        : entityType === 'purchasereturn'
                           ? 'Purchase Return'
                           : entityType === 'issueinventory'
                              ? 'Issue'
                              : entityType === 'receiveinventory'
                                 ? 'Receive'
                                 : entityType
            }
            entityType={entityType}
            singleEntity={viewDetail}
            setIsEditFormOpen={setIsEditFormOpen}
            setIsOpenSingleView={setIsOpenPurchaseReturnSingleView}
            isEditFormOpen={isEditFormOpen}
            viewDetail={viewDetail}
            lineItems={
               viewDetail?.lineitems ||
               viewDetail?.receiveLineDetail ||
               viewDetail?.purchasereturnlineitems ||
               viewDetail?.salesreturnlineitems ||
               viewDetail?.issueLineDetail ||
               []
            }
            billTerms={
               viewDetail?.purchasereturnbillteams ||
               viewDetail?.receiveLineDetail ||
               viewDetail?.salesreturnbillteams ||
               viewDetail?.purchasebillteams ||
               viewDetail?.salesbillteams ||
               []
            }
            onDelete={deletePurchaseReturn}
            handleSerchClose={handleClose}
         // handleViewSingleData={handleViewSingleData}
         />
      </>
   );
}
